import React, { useState } from "react";
import { useMediaQuery } from "../../../hooks";
import { Divider } from "../../divider";
import { Spacer } from "../../spacer";
import { PortalButton } from "./portal_button";
import styles from "./manage_membership_payment_method.module.css";
import { Icon } from "../../../components";
import { colors } from "@/offering_v2/components/colors";
import { useCreatePaymentIntent } from "../hooks/use_create_payment_intent";
import { useParams } from "react-router-dom";
import { PortalPathParams } from "../hooks/use_portal_history";
import { Partner } from "@/offering/api";
import { useSubscriptionDetailsQuery } from "../hooks/use_subscription_details_query";
import { PortalChangePaymentMethodModal } from "./portal_change_payment_method_modal";
import { PulseLoader } from "@/components/pulse_loader";
import { useMembershipManagementAnalytics } from "../hooks/use_membership_management_analytics";

interface ManageMembershipPaymentMethodProps {
  partner: Partner;
}

export function ManageMembershipPaymentMethod({
  partner,
}: ManageMembershipPaymentMethodProps) {
  const { device } = useMediaQuery();
  const { subscriptionId } = useParams<PortalPathParams>();

  const [openDialog, setOpenDialog] = useState(false);
  const [clientSecret, setClientSecret] = useState<string>();
  const [loadingClientSecret, setLoadingClientSecret] = useState(false);

  const analyticEvent = useMembershipManagementAnalytics();
  const createPaymentIntent = useCreatePaymentIntent();
  const {
    data: subscriptionDetailsData,
    revalidate: revalidateSubscriptionDetails,
  } = useSubscriptionDetailsQuery(partner.id, subscriptionId);
  const defaultCard = subscriptionDetailsData?.defaultPaymentMethod?.card;
  const isPaymentFailed =
    subscriptionDetailsData?.paymentStatus === "payment_failed";

  const handleClickChangePaymentMethod = async () => {
    analyticEvent(
      isPaymentFailed
        ? "Update Payment Method Click"
        : "Change Payment Method Click"
    );
    setLoadingClientSecret(true);
    const response = await createPaymentIntent(partner.id, subscriptionId);
    const clientSecret = response?.data.clientSecret;
    if (clientSecret) {
      setOpenDialog(true);
      setClientSecret(clientSecret);
    }
    setLoadingClientSecret(false);
  };

  const handleOnSucceed = () => {
    setOpenDialog(false);
    revalidateSubscriptionDetails();
  };

  return (
    <div>
      <div className="fs-3 fw-bold">Payment Method</div>
      <Spacer size={20} />
      <div className={styles.paymentMethodDetailsWrapper}>
        {defaultCard && (
          <div className={styles.paymentMethodDetails}>
            <img
              src={defaultCard.cardNetworkUrl}
              alt={defaultCard.brand}
              width={33}
              height={22}
            />
            <div>
              <b>
                <span className="text-capitalize">{defaultCard.brand}</span>{" "}
                ending in {defaultCard.last4}
              </b>
              <br />
              <Spacer size={4} />
              Expires {defaultCard.expMonth}/{defaultCard.expYear}
            </div>
          </div>
        )}
        {isPaymentFailed && device === "mobile" && (
          <div>
            <Spacer size={16} />
            <div className="d-flex gap-2 align-items-center">
              <div>
                <Icon name="filled-info" color="#FF595A" size={16} />
              </div>
              <div style={{ color: colors.grapefruitCore }}>
                Payment failed. Please update to maintain your membership.
              </div>
            </div>
          </div>
        )}
        {device === "mobile" && (
          <>
            <Spacer size={16} />
            <Divider />
            <Spacer size={16} />
          </>
        )}
        <div className={styles.centerBtn}>
          {loadingClientSecret ? (
            <PulseLoader />
          ) : (
            <PortalButton
              appearance="text"
              onClick={handleClickChangePaymentMethod}
            >
              {isPaymentFailed ? "Update" : "Change"}
            </PortalButton>
          )}
        </div>
      </div>
      {isPaymentFailed && device !== "mobile" && (
        <div>
          <Spacer size={16} />
          <div className="d-flex gap-2 align-items-center">
            <div>
              <Icon name="filled-info" color="#FF595A" size={16} />
            </div>
            <div style={{ color: colors.grapefruitCore }}>
              Payment failed. Please update to maintain your membership.
            </div>
          </div>
        </div>
      )}

      {clientSecret && (
        <PortalChangePaymentMethodModal
          show={openDialog}
          onHide={() => setOpenDialog(false)}
          onSucceed={handleOnSucceed}
          clientSecret={clientSecret}
          partnerId={partner.id}
          partnerSlug={partner.partnerSlug}
          subscriptionId={subscriptionId}
        />
      )}
    </div>
  );
}
